/**
 * External listing posts — where a listing is posted on Facebook Marketplace / Craigslist.
 * Endpoints: apps/api/src/routes/manager/external-listing-posts.routes.ts.
 */
import { z } from 'zod';
import {
  ListingSiteSchema,
  type ExternalListingPostDto,
  type ListExternalPostsResponse,
  type RecordExternalPostRequest,
  type UpdateExternalPostRequest,
} from '@resida/api-types/listing-extension';
import { apiFetch } from './api.core';

// Derived from the shared boundary schemas in @resida/api-types/listing-extension.
export type { ExternalListingPostDto };
export type ListingSiteDto = z.infer<typeof ListingSiteSchema>;

/** Landlord-facing site names (shared with the API and the extension panel). */
export { SITE_LABELS as LISTING_SITE_LABELS } from '@resida/shared';

/** Query key for a listing's external posts (invalidated by the `external_listing_post.changed` event). */
export const externalPostsKey = (listingId: string) => ['listing', listingId, 'external-posts'] as const;

export async function listExternalPosts(listingId: string): Promise<ExternalListingPostDto[]> {
  const res = await apiFetch<ListExternalPostsResponse>(`/landlord/api/listings/${listingId}/external-posts`);
  return res.posts;
}

export async function recordExternalPost(
  listingId: string,
  body: RecordExternalPostRequest,
): Promise<ExternalListingPostDto> {
  return apiFetch(`/landlord/api/listings/${listingId}/external-posts`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function markExternalPostRemoved(postId: string): Promise<ExternalListingPostDto> {
  const body: UpdateExternalPostRequest = { status: 'removed' };
  return apiFetch(`/landlord/api/external-listing-posts/${postId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function deleteExternalPost(postId: string): Promise<void> {
  await apiFetch(`/landlord/api/external-listing-posts/${postId}`, { method: 'DELETE' });
}
